import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { ArrowRight, LucideIcon } from "lucide-react";
import { useParallaxTransform } from "@/hooks/useParallaxTransform";

interface HeroSectionProps {
  badge: {
    icon: LucideIcon;
    text: string;
  };
  title: string;
  titleHighlight: string;
  description: string;
  primaryCTA: {
    text: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    onClick?: () => void;
  };
  gradient: string;
  accentColor: string;
  orbitIcons: LucideIcon[];
  logo?: {
    src: string;
    alt: string;
  };
  backgroundImage?: string;
}

// Color mappings for dynamic accent colors
const accentColorMap: Record<string, { badge: string; text: string; gradient: string; button: string; description: string }> = {
  blue: {
    badge: "text-blue-300",
    text: "text-white",
    gradient: "from-blue-400 via-blue-300 to-cyan-400",
    button: "text-blue-900 hover:bg-blue-50",
    description: "text-white/90",
  },
  orange: {
    badge: "text-orange-300",
    text: "text-white",
    gradient: "from-orange-400 via-amber-300 to-yellow-400",
    button: "text-orange-900 hover:bg-orange-50",
    description: "text-white/90",
  },
  purple: {
    badge: "text-purple-400",
    text: "text-purple-200",
    gradient: "from-purple-400 via-violet-300 to-purple-400",
    button: "text-purple-900 hover:bg-purple-50",
    description: "text-purple-100/80",
  },
  green: {
    badge: "text-green-400",
    text: "text-green-200",
    gradient: "from-green-400 via-emerald-300 to-teal-400",
    button: "text-green-900 hover:bg-green-50",
    description: "text-green-100/80",
  },
  cyan: {
    badge: "text-cyan-300",
    text: "text-white",
    gradient: "from-cyan-400 via-sky-300 to-blue-400",
    button: "text-cyan-900 hover:bg-cyan-50",
    description: "text-white/90",
  },
  pink: {
    badge: "text-pink-400",
    text: "text-pink-200",
    gradient: "from-pink-400 via-rose-300 to-pink-400",
    button: "text-pink-900 hover:bg-pink-50",
    description: "text-pink-100/80",
  },
  indigo: {
    badge: "text-indigo-400",
    text: "text-indigo-200",
    gradient: "from-indigo-400 via-violet-300 to-purple-400",
    button: "text-indigo-900 hover:bg-indigo-50",
    description: "text-indigo-100/80",
  },
  amber: {
    badge: "text-amber-400",
    text: "text-amber-200",
    gradient: "from-amber-400 via-yellow-300 to-orange-400",
    button: "text-amber-900 hover:bg-amber-50",
    description: "text-amber-100/80",
  },
};

export const HeroSection = ({
  badge,
  title,
  titleHighlight,
  description,
  primaryCTA,
  secondaryCTA,
  gradient,
  accentColor,
  orbitIcons,
  logo,
  backgroundImage,
}: HeroSectionProps) => {
  const BadgeIcon = badge.icon;
  const colors = accentColorMap[accentColor] || accentColorMap.blue;

  // Smooth parallax for background image without React re-renders
  const bgImageRef = useRef<HTMLDivElement>(null);
  useParallaxTransform(bgImageRef, {
    speed: 0.4,
    direction: "down",
    disabled: !backgroundImage,
    maxOffset: 200,
  });

  return (
    <section className="relative pt-20 pb-16 sm:pt-24 sm:pb-20 md:pt-32 md:pb-28 overflow-hidden">
      {/* Background image layer (transform parallax for smoothness + mobile support) */}
      {backgroundImage && (
        <div 
          ref={bgImageRef}
          aria-hidden="true"
          className="pointer-events-none absolute -inset-32 bg-cover bg-center bg-no-repeat will-change-transform"
          style={{ 
            backgroundImage: `url(${backgroundImage})`,
            transform: "translate3d(0, 0, 0)",
          }}
        />
      )}
      
      {/* Animated gradient background - overlay when image exists */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} ${backgroundImage ? 'opacity-80' : ''}`} />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Orbiting icons */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-20 hidden sm:block">
        {orbitIcons.map((Icon, index) => (
          <div
            key={index}
            className="absolute w-12 h-12 text-white/60"
            style={{
              animation: `orbit ${20 + index * 5}s linear infinite`,
              transformOrigin: '300px 300px',
              left: '50%',
              top: '50%',
              marginLeft: '-24px',
              marginTop: '-24px',
            }}
          >
            <Icon className="w-full h-full" />
          </div>
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo with glow effect */}
          {logo && (
            <AnimateOnScroll animation="scale-in">
              <div className="relative inline-block mb-8">
                {/* Glow effect */}
                <div 
                  className="absolute inset-0 blur-3xl opacity-60 scale-150"
                  style={{
                    background: `radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)`
                  }}
                />
                <img 
                  src={logo.src} 
                  alt={logo.alt}
                  className="relative h-20 sm:h-24 md:h-28 mx-auto drop-shadow-2xl"
                />
              </div>
            </AnimateOnScroll>
          )}

          <AnimateOnScroll animation="fade-up">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 sm:mb-6">
              <BadgeIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${colors.badge}`} />
              <span className={`${colors.text} text-xs sm:text-sm font-medium`}>{badge.text}</span>
            </div>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fade-up" delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
              {title}
              <span className={`block bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
                {titleHighlight}
              </span>
            </h1>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fade-up" delay={200}>
            <p className={`text-base sm:text-lg md:text-xl ${colors.description} mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-2`}>
              {description}
            </p>
          </AnimateOnScroll>
          
          <AnimateOnScroll animation="fade-up" delay={300}>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
              <Button 
                size="lg" 
                className={`bg-white ${colors.button} px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold shadow-2xl w-full sm:w-auto`}
                onClick={primaryCTA.onClick}
              >
                {primaryCTA.text}
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              {secondaryCTA && (
                <Button 
                  size="lg" 
                  className="border-2 border-white/40 bg-white/10 text-white hover:bg-white/20 px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg backdrop-blur-sm font-medium w-full sm:w-auto"
                  onClick={secondaryCTA.onClick}
                >
                  {secondaryCTA.text}
                </Button>
              )}
            </div>
          </AnimateOnScroll>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />

      <style>{`
        @keyframes orbit {
          from { transform: rotate(0deg) translateX(150px) rotate(0deg); }
          to { transform: rotate(360deg) translateX(150px) rotate(-360deg); }
        }
      `}</style>
    </section>
  );
};
